import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const TechnologySoftware = () => {
  return (
    <>
      <SEO 
        title="Technology & Software Solutions - Enterprise AI & Development Services"
        description="Empower your technology company with AI-driven development tools, DevOps automation, cloud-native architecture, and scalable software solutions from Abacies Logiciels."
        keywords="technology solutions, software development, DevOps automation, cloud native, API development, microservices, AI for tech companies, SaaS solutions"
      />

      {/* Hero Section */}
      <section className="relative pt-10 pb-10 md:pt-10 md:pb-10 bg-gradient-to-br from-pearl-white via-alabaster to-blush-light/10 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E63946' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-primary-red/20 mb-6 animate-fade-in">
              <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span className="text-sm font-semibold text-charcoal">Industry Solutions</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Accelerate Innovation in <span className="text-gradient">Technology & Software</span>
            </h1>
            
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Transform your software development lifecycle with AI-powered tools, intelligent automation, and cutting-edge DevOps solutions designed for modern technology companies.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Solutions
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum">
                <div className="text-3xl font-bold text-primary-red mb-1">3x</div>
                <div className="text-sm text-titanium">Faster Development</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum">
                <div className="text-3xl font-bold text-primary-red mb-1">60%</div>
                <div className="text-sm text-titanium">Cost Reduction</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum">
                <div className="text-3xl font-bold text-primary-red mb-1">99.9%</div>
                <div className="text-sm text-titanium">System Uptime</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum">
                <div className="text-3xl font-bold text-primary-red mb-1">85%</div>
                <div className="text-sm text-titanium">Faster TTM</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Overview */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                Powering the Next Generation of Software Innovation
              </h2>
              <p className="text-titanium mb-6">
                The technology and software industry demands constant innovation, rapid deployment, and flawless execution. In an environment where speed-to-market and code quality can make or break a product, having the right tools and processes is critical.
              </p>
              <p className="text-titanium mb-6">
                Abacies Logiciels empowers technology companies with AI-driven development tools, intelligent DevOps automation, and scalable cloud-native architectures that accelerate innovation while maintaining the highest standards of quality and security.
              </p>
              <div className="flex items-start space-x-4 p-4 bg-blush-light/20 rounded-lg">
                <svg className="w-6 h-6 text-primary-red flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <p className="text-sm text-graphite">
                  <strong className="text-charcoal">Industry Insight:</strong> Companies leveraging AI-powered development tools report 3x faster feature deployment and 60% reduction in technical debt.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-2xl p-6 border border-primary-red/20">
                <svg className="w-10 h-10 text-primary-red mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h3 className="text-lg font-semibold text-charcoal mb-2">Rapid Deployment</h3>
                <p className="text-sm text-titanium">CI/CD pipelines with AI-powered testing and validation</p>
              </div>
              <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-2xl p-6 border border-primary-red/20 mt-8">
                <svg className="w-10 h-10 text-primary-red mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <h3 className="text-lg font-semibold text-charcoal mb-2">Code Quality</h3>
                <p className="text-sm text-titanium">AI-driven code review and quality assurance</p>
              </div>
              <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-2xl p-6 border border-primary-red/20">
                <svg className="w-10 h-10 text-primary-red mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
                <h3 className="text-lg font-semibold text-charcoal mb-2">Cloud Native</h3>
                <p className="text-sm text-titanium">Scalable microservices and container orchestration</p>
              </div>
              <div className="bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-2xl p-6 border border-primary-red/20 mt-8">
                <svg className="w-10 h-10 text-primary-red mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="text-lg font-semibold text-charcoal mb-2">Performance Monitoring</h3>
                <p className="text-sm text-titanium">Real-time analytics and predictive insights</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Challenges */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Overcoming Critical Technology Challenges
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Address the most pressing challenges facing modern software development teams
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
                title: "Accelerated Time-to-Market",
                challenge: "Pressure to release features faster while maintaining quality",
                solution: "AI-powered CI/CD pipelines that automate testing, deployment, and rollback processes"
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                title: "Technical Debt Management",
                challenge: "Accumulating legacy code that slows development",
                solution: "Automated code refactoring tools and AI-driven architecture optimization"
              },
              {
                icon: "M13 10V3L4 14h7v7l9-11h-7z",
                title: "Infrastructure Scalability",
                challenge: "Handling unpredictable traffic spikes and growth",
                solution: "Auto-scaling cloud architecture with predictive resource allocation"
              },
              {
                icon: "M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z",
                title: "Security & Compliance",
                challenge: "Meeting stringent security standards while innovating",
                solution: "Integrated security testing, vulnerability scanning, and compliance automation"
              },
              {
                icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                title: "Team Collaboration",
                challenge: "Coordinating distributed development teams",
                solution: "AI-enhanced collaboration tools with intelligent code review and pair programming"
              },
              {
                icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
                title: "Performance Optimization",
                challenge: "Maintaining optimal performance at scale",
                solution: "AI-powered performance monitoring with predictive bottleneck identification"
              }
            ].map((item, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300 group">
                <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-colors">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{item.title}</h3>
                <p className="text-sm text-titanium mb-3">
                  <strong className="text-graphite">Challenge:</strong> {item.challenge}
                </p>
                <p className="text-sm text-primary-red">
                  <strong>Solution:</strong> {item.solution}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Solutions */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Comprehensive Solutions for Technology Companies
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              End-to-end technology services designed to accelerate your software development lifecycle
            </p>
          </div>

          <div className="space-y-8">
            {[
              {
                title: "AI-Powered Development Tools",
                description: "Intelligent code generation, completion, and review",
                features: [
                  "Natural language to code conversion with GPT-4 integration",
                  "Automated code review and quality scoring",
                  "Intelligent bug detection and fix suggestions",
                  "Context-aware code completion and documentation generation"
                ],
                metrics: { label: "Productivity Increase", value: "45%" }
              },
              {
                title: "DevOps Automation & CI/CD",
                description: "Streamlined deployment pipelines with zero-touch automation",
                features: [
                  "Automated build, test, and deployment workflows",
                  "AI-driven test case generation and prioritization",
                  "Intelligent rollback and disaster recovery",
                  "Multi-cloud deployment orchestration"
                ],
                metrics: { label: "Deployment Frequency", value: "10x" }
              },
              {
                title: "Cloud-Native Architecture",
                description: "Scalable microservices and container orchestration",
                features: [
                  "Kubernetes cluster management and optimization",
                  "Service mesh implementation for microservices",
                  "Serverless architecture design and deployment",
                  "Auto-scaling policies with predictive analytics"
                ],
                metrics: { label: "Infrastructure Cost Savings", value: "40%" }
              },
              {
                title: "API Development & Management",
                description: "Comprehensive API lifecycle management platform",
                features: [
                  "RESTful and GraphQL API design and implementation",
                  "API gateway configuration and security",
                  "Rate limiting and usage analytics",
                  "Automated API documentation and versioning"
                ],
                metrics: { label: "API Response Time", value: "<100ms" }
              },
              {
                title: "Code Quality & Security",
                description: "Continuous security scanning and vulnerability management",
                features: [
                  "Static and dynamic code analysis",
                  "Dependency vulnerability scanning",
                  "Secrets detection and management",
                  "Compliance reporting and audit trails"
                ],
                metrics: { label: "Security Issues Detected", value: "95%" }
              },
              {
                title: "Performance Monitoring & Analytics",
                description: "Real-time application performance monitoring and optimization",
                features: [
                  "Distributed tracing and log aggregation",
                  "Custom metrics and alerting",
                  "Performance bottleneck identification",
                  "User experience monitoring and analytics"
                ],
                metrics: { label: "MTTR Reduction", value: "70%" }
              }
            ].map((solution, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="grid lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-3">
                    <h3 className="text-2xl font-bold mb-3 text-charcoal">{solution.title}</h3>
                    <p className="text-titanium mb-4">{solution.description}</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {solution.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <svg className="w-5 h-5 text-primary-red mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm text-graphite">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="lg:col-span-1 flex items-center justify-center">
                    <div className="text-center p-6 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-xl border border-primary-red/20">
                      <div className="text-4xl font-bold text-primary-red mb-2">{solution.metrics.value}</div>
                      <div className="text-sm text-titanium">{solution.metrics.label}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Real-World Applications
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              See how leading technology companies leverage our solutions
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "SaaS Platform Development",
                company: "Enterprise Software Vendor",
                challenge: "Needed to reduce feature development time while maintaining multi-tenant security",
                solution: "Implemented AI-powered development tools and automated testing framework",
                results: [
                  "50% faster feature releases",
                  "Zero security breaches in 18 months",
                  "90% test coverage automated"
                ]
              },
              {
                title: "E-commerce Platform Scaling",
                company: "Online Marketplace",
                challenge: "Infrastructure couldn't handle Black Friday traffic spikes",
                solution: "Deployed cloud-native architecture with predictive auto-scaling",
                results: [
                  "99.99% uptime during peak seasons",
                  "60% reduction in infrastructure costs",
                  "5x traffic handling capacity"
                ]
              },
              {
                title: "Mobile App Backend Optimization",
                company: "Social Media Startup",
                challenge: "API response times affecting user experience",
                solution: "Implemented microservices architecture with intelligent caching",
                results: [
                  "75% improvement in API response time",
                  "10x increase in concurrent users",
                  "40% reduction in server costs"
                ]
              },
              {
                title: "DevOps Transformation",
                company: "Financial Technology Company",
                challenge: "Manual deployment process causing frequent production issues",
                solution: "Built comprehensive CI/CD pipeline with automated rollback",
                results: [
                  "From weekly to hourly deployments",
                  "95% reduction in deployment failures",
                  "80% faster incident resolution"
                ]
              }
            ].map((useCase, index) => (
              <div key={index} className="card hover:shadow-xl transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-charcoal">{useCase.title}</h3>
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-primary-red font-semibold mb-3">{useCase.company}</p>
                
                <div className="space-y-3 mb-4">
                  <div>
                    <p className="text-xs font-semibold text-graphite mb-1">Challenge</p>
                    <p className="text-sm text-titanium">{useCase.challenge}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-graphite mb-1">Solution</p>
                    <p className="text-sm text-titanium">{useCase.solution}</p>
                  </div>
                </div>

                <div className="pt-4 border-t border-platinum">
                  <p className="text-xs font-semibold text-graphite mb-2">Results</p>
                  <div className="space-y-1">
                    {useCase.results.map((result, idx) => (
                      <div key={idx} className="flex items-start">
                        <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="text-sm text-charcoal">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Our Technology Ecosystem
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              We work with the leading technologies and frameworks in the software development ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="card text-center">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">Cloud Platforms</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>AWS, Azure, GCP</p>
                <p>Kubernetes, Docker</p>
                <p>Terraform, CloudFormation</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">Development</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>React, Angular, Vue.js</p>
                <p>Node.js, Python, Go</p>
                <p>GraphQL, REST APIs</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">DevOps Tools</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>Jenkins, GitLab CI</p>
                <p>Ansible, Chef, Puppet</p>
                <p>Prometheus, Grafana</p>
              </div>
            </div>
            <div className="card text-center">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">AI & ML</h3>
              <div className="space-y-2 text-sm text-titanium">
                <p>TensorFlow, PyTorch</p>
                <p>GPT-4, Claude, Gemini</p>
                <p>MLflow, Kubeflow</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-alabaster to-blush-light/20">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Why Choose Abacies for Your Technology Needs
              </h2>
              <p className="text-xl text-titanium">
                Partner with experts who understand the unique challenges of software development
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: "M13 10V3L4 14h7v7l9-11h-7z",
                  title: "Accelerated Innovation",
                  description: "Ship features 3x faster with AI-powered development tools and automated workflows"
                },
                {
                  icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
                  title: "Cost Optimization",
                  description: "Reduce infrastructure and operational costs by up to 60% through intelligent resource management"
                },
                {
                  icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                  title: "Enterprise Security",
                  description: "Built-in security best practices and compliance automation for peace of mind"
                },
                {
                  icon: "M13 7h8m0 0v8m0-8l-8 8-4-4-6 6",
                  title: "Scalability",
                  description: "Architecture designed to scale from startup to enterprise without major rewrites"
                },
                {
                  icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
                  title: "Expert Team",
                  description: "Work with engineers who have built and scaled systems at top tech companies"
                },
                {
                  icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z",
                  title: "Proven Track Record",
                  description: "Successfully delivered 200+ projects for technology companies worldwide"
                }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-xl border border-platinum hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">{benefit.title}</h3>
                    <p className="text-sm text-titanium">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-gradient-to-r from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Software Development?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Join leading technology companies that trust Abacies to accelerate their innovation and scale their platforms efficiently.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule a Consultation
            </Link>
            <Link to="/services" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore Our Services
            </Link>
          </div>
          <p className="mt-8 text-sm opacity-75">
            Free technical assessment • No commitment required • Expert guidance
          </p>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-5 bg-alabaster">
        <div className="page-container">
          <h3 className="text-2xl font-bold mb-8 text-charcoal text-center">Related Industry Solutions</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/solutions/financial-services" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h4 className="font-semibold text-charcoal mb-2">Financial Services</h4>
              <p className="text-sm text-titanium">FinTech and banking solutions</p>
            </Link>
            <Link to="/solutions/retail-ecommerce" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h4 className="font-semibold text-charcoal mb-2">Retail & E-commerce</h4>
              <p className="text-sm text-titanium">Digital commerce platforms</p>
            </Link>
            <Link to="/solutions/healthcare-life-sciences" className="card hover:shadow-xl transition-all duration-300 text-center">
              <h4 className="font-semibold text-charcoal mb-2">Healthcare</h4>
              <p className="text-sm text-titanium">HealthTech innovations</p>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default TechnologySoftware;